import React, { useEffect, useRef } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import { socialItems } from "@/lib/social";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, X, Minus, Plus, Trash2, ChevronRight, Store as StoreIcon, MapPin, Mail, ShieldCheck, Truck, BadgeCheck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/storeutils";
import { PharmacyHeader, PharmacyFooter } from "@/components/pharmacy/PharmacyParts";
import { MediHeader, MediFooter } from "@/components/pharmacy/MediParts";
import { BookHeader, BookFooter } from "@/components/books/BookParts";
import { InkHeader, InkFooter } from "@/components/books/BootinParts";
import { VerdantHeader, VerdantFooter } from "@/components/verdant/VerdantParts";
import { AromaHeader, AromaFooter } from "@/components/aroma/AromaParts";
import { MarketHeader, MarketFooter } from "@/components/market/MarketParts";
import { MeatHeader, MeatFooter } from "@/components/meat/MeatParts";
import { ArtisanHeader, ArtisanFooter } from "@/components/artisan/ArtisanParts";
import { verdantVars, mediVars } from "@/lib/themeVars";
import { useProductTones } from "@/lib/productTones";
import MobileNav from "@/components/storefront/MobileNav";
import { cartItemTax, sumTax } from "@/lib/tax";
import { navLinks, footerColumns, trustItems, footerLines, scrollToHash, isNavActive } from "@/lib/storenav";

export default function StoreShell({ children }) {
  const ctx = useStore();
  const { store, c, font, dark, border, muted, slug } = ctx;
  const cart = useCart();
  const { hash, pathname } = useLocation();
  // footer/menu links like /store/slug#deals should land on the section once the page has rendered
  useEffect(() => { if (hash && store) { const t = setTimeout(() => scrollToHash(hash), 120); return () => clearTimeout(t); } return undefined; }, [hash, pathname, store]);
  const shellRef = useRef(null);
  useProductTones(shellRef, ctx.products, !!store);   // transparent product photos get a tonal backdrop in every theme
  if (!store) return null;
  const layout = ctx.theme?.layout;
  const medi = layout === "medi";
  const books = layout === "books";
  const ink = layout === "bookish";
  const verdant = layout === "verdant";
  const aroma = layout === "aroma";
  const market = layout === "market";
  const artisan = layout === "artisan";
  const meat = layout === "meatverse";
  const rx = layout === "pharmacy" || medi || books || ink || verdant || aroma || market || artisan || meat;

  return (
    <div ref={shellRef} className={verdant ? "rx-store verdant-store" : rx ? "rx-store" : undefined} style={{ background: c.bg, color: c.text, fontFamily: font, minHeight: "100vh", ...(books || ink || verdant || aroma || market || artisan || meat ? { overflowX: "clip" } : {}), ...(meat ? { "--mv-primary": c.primary, "--mv-accent": c.accent, "--k-primary": c.primary, "--k-accent": c.accent } : {}), ...(verdant ? { "--v-primary": c.primary, "--v-lemon": c.accent } : {}), ...(aroma ? { "--a-ink": c.primary, "--a-accent": c.accent } : {}), ...(market ? { "--k-primary": c.primary, "--k-accent": c.accent } : {}), ...(artisan ? { "--z-primary": c.primary, "--z-accent": c.accent } : {}), ...(store.theme_colors?.primary ? (verdant ? verdantVars(c.primary) : medi ? mediVars(c.primary) : {}) : {}) }} data-testid="store-shell">
      {rx && (meat ? <MeatHeader s={ctx} cart={cart} /> : artisan ? <ArtisanHeader s={ctx} cart={cart} /> : market ? <MarketHeader s={ctx} cart={cart} /> : aroma ? <AromaHeader s={ctx} cart={cart} /> : verdant ? <VerdantHeader s={ctx} cart={cart} /> : ink ? <InkHeader s={ctx} cart={cart} /> : books ? <BookHeader s={ctx} cart={cart} /> : medi ? <MediHeader s={ctx} cart={cart} /> : <PharmacyHeader s={ctx} cart={cart} />)}
      {!rx && <header className="sticky top-0 z-30 backdrop-blur border-b" style={{ background: c.surface + "f2", borderColor: border }}>
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <Link to={`/store/${slug}`} className="flex items-center gap-2 font-extrabold text-xl" data-testid="storefront-name" style={{ fontFamily: font }}>
            <StoreLogo store={store} fallback={<><span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: c.primary, color: "#fff" }}><StoreIcon size={17} /></span>
            {store.name}</>} />
          </Link>
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium" style={{ color: muted }} data-testid="storefront-nav">
            {navLinks(slug).map(([label, to]) => { const on = isNavActive(pathname, slug, to); return (
              <Link key={label} to={to} aria-current={on ? "page" : undefined} className="hover:opacity-70 transition-opacity pb-0.5" style={on ? { color: c.text, fontWeight: 700, borderBottom: `2px solid ${c.accent}` } : undefined} data-testid={label === "Track Order" ? "storefront-track-link" : undefined}>{label}</Link>
            ); })}
          </nav>
          <div className="flex items-center gap-2 ml-auto lg:ml-0">
            <MobileNav slug={slug} color={c.text} bg={c.surface} accent={c.accent} border={border} />
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn"
            className="relative rounded-full p-2.5 transition-transform hover:scale-105" style={{ background: c.accent, color: "#fff" }}>
            <ShoppingCart size={18} />
            {cart.count > 0 && <span className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: "#dc2626", color: "#fff" }}>{cart.count}</span>}
          </button>
          </div>
        </div>
      </header>}

      {children}

      {rx ? (meat ? <MeatFooter s={ctx} /> : artisan ? <ArtisanFooter s={ctx} /> : market ? <MarketFooter s={ctx} /> : aroma ? <AromaFooter s={ctx} /> : verdant ? <VerdantFooter s={ctx} /> : ink ? <InkFooter s={ctx} /> : books ? <BookFooter s={ctx} /> : medi ? <MediFooter s={ctx} /> : <PharmacyFooter s={ctx} />) : <ClassicFooter s={ctx} />}

      <CartDrawer c={c} border={border} muted={muted}
        onCheckout={() => { cart.setOpen(false); navigateTo(`/store/${slug}/checkout`); }} />
    </div>
  );
}

function ClassicFooter({ s }) {
  const { store, slug, c, font, border, muted, dark } = s;
  const lines = footerLines(store);
  const icons = { money: BadgeCheck, verified: ShieldCheck, delivery: Truck };
  return (
    <footer style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", borderTop: `1px solid ${border}` }} data-testid="storefront-footer">
      <div className="max-w-6xl mx-auto px-5 py-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {trustItems(s.store.requires_prescription, s.store.free_shipping_over).map(([k, h, d]) => { const I = icons[k]; return (
          <div key={h} className="flex items-center gap-3 justify-center text-[13px]"><I size={22} style={{ color: c.accent }} /><div><b style={{ fontFamily: font }}>{h}</b><div className="text-[11px]" style={{ color: muted }}>{d}</div></div></div>
        ); })}
      </div>
      <div className="max-w-6xl mx-auto px-5 py-10 grid md:grid-cols-5 gap-8 border-t" style={{ borderColor: border }}>
        <div className="md:col-span-2">
          <StoreLogo store={store} height={46} fallback={<div className="font-extrabold text-xl uppercase" style={{ fontFamily: font }}>{store.name}</div>} />
          <p className="text-[13px] mt-3 flex gap-2" style={{ color: muted }}><MapPin size={14} className="mt-0.5 shrink-0" style={{ color: c.accent }} />{lines.location}</p>
          <p className="text-[13px] mt-1.5 flex gap-2" style={{ color: muted }}><Mail size={14} className="mt-0.5 shrink-0" style={{ color: c.accent }} />{lines.support}</p>
          <div className="flex gap-2.5 mt-4">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.05)" }}><I.icon size={14} /></a>)}</div>
        </div>
        {footerColumns(slug).map(([h, links]) => (
          <div key={h}>
            <div className="font-bold text-sm mb-3" style={{ fontFamily: font }}>{h}</div>
            <ul className="space-y-2 text-[13px]" style={{ color: muted }}>{links.map(([l, to]) => <li key={l}><Link to={to} className="hover:opacity-70">{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="text-center text-xs py-5 border-t" style={{ borderColor: border, color: muted }}>
        © 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link>
      </div>
    </footer>
  );
}

let _navigate;
function CartDrawer({ c, border, muted, onCheckout }) {
  const cart = useCart();
  const sref = useStore();
  const taxLines = cart.items.map((i) => cartItemTax(i, sref.products));
  const tx = sumTax(taxLines);
  _navigate = useNavigate();
  if (!cart.open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={() => cart.setOpen(false)}>
      <div className="absolute inset-0 bg-black/60 rx-fade" />
      <div className="relative w-full max-w-md h-full overflow-y-auto scrollbar-thin flex flex-col rx-drawer" style={{ background: c.surface, color: c.text }} onClick={(e) => e.stopPropagation()} data-testid="storefront-cart-drawer">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: border }}>
          <h2 className="font-bold text-lg">Your cart ({cart.count})</h2>
          <button onClick={() => cart.setOpen(false)} aria-label="Close cart"><X size={20} /></button>
        </div>
        <div className="flex-1 p-5 space-y-3">
          {cart.items.length === 0 ? (
            <p className="text-center py-10" style={{ color: muted }}>Your cart is empty.</p>
          ) : cart.items.map((it, idx) => { const t = taxLines[idx]; return (
            <div key={it.lineId} className="flex gap-3 items-center" data-testid={`cart-item-${it.product_id}`}>
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0" style={{ background: "#f1f5f9" }}>
                {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm line-clamp-1">{it.name}</div>
                {it.variant && <div className="text-xs" style={{ color: muted }}>{it.variant}</div>}
                <div className="text-sm font-bold" style={{ color: c.primary }}>{inr(it.price)}</div>
                <div className="mt-1 grid grid-cols-3 gap-1 text-[10px] leading-tight" style={{ color: muted }} data-testid={`cart-tax-${it.product_id}`}>
                  <span>Price<b className="block text-[11px]" style={{ color: c.text }}>{inr(t.line)}</b></span>
                  <span>Tax{t.rate ? ` ${t.rate}%` : ""}{t.inclusive && t.tax ? " (incl.)" : ""}<b className="block text-[11px]" style={{ color: c.text }}>{t.tax ? inr(t.tax) : "—"}</b></span>
                  <span>Total<b className="block text-[11px]" style={{ color: c.text }}>{inr(t.total)}</b></span>
                </div>
              </div>
              <div className="flex items-center rounded-full border" style={{ borderColor: border }}>
                <button onClick={() => cart.updateQty(it.lineId, it.quantity - 1)} className="p-1.5" aria-label="Decrease"><Minus size={12} /></button>
                <span className="px-1.5 text-sm font-mono">{it.quantity}</span>
                <button onClick={() => cart.updateQty(it.lineId, it.quantity + 1)} className="p-1.5" aria-label="Increase"><Plus size={12} /></button>
              </div>
              <button onClick={() => cart.remove(it.lineId)} className="opacity-60 hover:opacity-100" aria-label="Remove"><Trash2 size={15} /></button>
            </div>
          ); })}
        </div>
        {cart.items.length > 0 && (
          <div className="p-5 border-t" style={{ borderColor: border }}>
            <div className="space-y-1 mb-3 text-sm">
              <div className="flex justify-between" style={{ color: muted }}><span>Items</span><span>{inr(tx.subtotal)}</span></div>
              {tx.added > 0 && <div className="flex justify-between" style={{ color: muted }}><span>Tax ({tx.label})</span><span>+ {inr(tx.added)}</span></div>}
              {tx.included > 0 && <div className="flex justify-between text-xs" style={{ color: muted }}><span>Includes {tx.label} of</span><span>{inr(tx.included)}</span></div>}
              <div className="flex justify-between font-bold pt-1 border-t" style={{ borderColor: border }} data-testid="cart-drawer-total"><span>Total</span><span>{inr(tx.subtotal + tx.added)}</span></div>
            </div>
            <button onClick={onCheckout} data-testid="storefront-cart-checkout-btn"
              className="w-full py-3 rounded-full font-bold flex items-center justify-center gap-2 hover:opacity-90"
              style={{ background: c.accent, color: "#fff" }}>
              Proceed to Checkout <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function navigateTo(path) { if (_navigate) _navigate(path); }
